import {
  httpsCallable,
  type HttpsCallableResult,
} from 'firebase/functions';

import { functions } from '../firebaseFunctions';
import {
  THEA_WEB_DISPATCHER,
  THEA_WEB_AI_DISPATCHER,
  type TheaWebOp,
  type TheaWebAIOp,
  type TheaWebDispatchEnvelope,
  type TheaWebMutateRecommendationRequest,
  type TheaWebMutateRecommendationResponse,
  type TheaWebMergeGiftFlowRequest,
  type TheaWebMergeGiftFlowResponse,
  type TheaWebMintMergeTokenRequest,
  type TheaWebMintMergeTokenResponse,
  type TheaWebRecordActivityRequest,
  type TheaWebRecordActivityResponse,
  type TheaWebRecordAddedLinkRequest,
  type TheaWebRecordAddedLinkResponse,
  type TheaWebSimilarProductsRequest,
  type TheaWebSimilarProductsResponse,
  type TheaWebSearchGiftsRequest,
  type TheaWebSearchGiftsResponse,
  type TheaWebSearchGuidesRequest,
  type TheaWebSearchGuidesResponse,
  type TheaWebCollectionGiftsRequest,
  type TheaWebCollectionGiftsResponse,
  type TheaWebSubmitGiftFlowRequest,
  type TheaWebSubmitGiftFlowResponse,
  type TheaWebCreateRecipientRequest,
  type TheaWebCreateRecipientResponse,
  type TheaWebUpdateRecipientRequest,
  type TheaWebUpdateRecipientResponse,
  type TheaWebCreateShareLinkRequest,
  type TheaWebCreateShareLinkResponse,
  type TheaWebGetSharedBoardRequest,
  type TheaWebGetSharedBoardResponse,
  type TheaWebRevokeShareLinkRequest,
  type TheaWebRevokeShareLinkResponse,
  type TheaWebTrackSignupRequest,
  type TheaWebTrackSignupResponse,
  type TheaWebCaptureEmailRequest,
  type TheaWebCaptureEmailResponse,
  type TheaWebSyncTimezoneRequest,
  type TheaWebSyncTimezoneResponse,
  type TheaWebMintResumeTokenRequest,
  type TheaWebMintResumeTokenResponse,
  type TheaWebRedeemResumeTokenRequest,
  type TheaWebRedeemResumeTokenResponse,
  type TheaWebRequestEmailOtpRequest,
  type TheaWebRequestEmailOtpResponse,
  type TheaWebVerifyEmailOtpRequest,
  type TheaWebVerifyEmailOtpResponse,
  type TheaWebGetClaimPreviewRequest,
  type TheaWebGetClaimPreviewResponse,
  type TheaWebClaimBoardRequest,
  type TheaWebClaimBoardResponse,
  type TheaWebRequestAccountDeletionRequest,
  type TheaWebRequestAccountDeletionResponse,
  type TheaWebConfirmAccountDeletionRequest,
  type TheaWebConfirmAccountDeletionResponse,
  type TheaWebResolveConsentGeoRequest,
  type TheaWebResolveConsentGeoResponse,
  type TheaWebGetBoardUsageRequest,
  type TheaWebGetBoardUsageResponse,
  type TheaWebGetLaunchFlagsRequest,
  type TheaWebGetLaunchFlagsResponse,
} from './schemas';
import type {
  TheaWebUpdateAlertRequest,
  TheaWebUpdateAlertResponse,
} from './schemas/alert';

// ---------------------------------------------------------------------------
// Dispatcher plumbing (2026-07 cost audit)
//
// Every lean callable below routes through the single warm `theaWeb` function
// as `{op, payload}` — one warm instance covers all the latency-critical
// paths instead of one $6.60/mo `min_instances=1` service per endpoint. The
// envelope lives entirely inside this module: each wrapper keeps its exact
// name, request/response types, per-op client timeout, and call signature,
// so call sites (hooks, auth flows) are byte-identical to before.
//
// The two AI heavyweights (getCarouselFeed, mutateRecommendation) route the
// same way but through a SECOND warm function, `theaWebAI` — they need
// 1GB/300s/Gemini+Typesense on the BE, which has no business on the lean warm
// path. Enrolled 2026-09-01: both were cold, and an "Update picks" chip-add
// measured 117.83s cold vs 0.16s warm.
//
// No callable stays on a dedicated function any more. `similarProducts` was
// the last holdout, on the grounds that its BE source had been deleted while
// the deployed service lived on — wrong in the end, and expensively so: the
// service had been pruned too, so the rail's fallback was calling a 404 and
// rendering blank. It is a dispatcher op as of 2026-09-22 (THE-482).
// `logEvents` still goes direct-to-endpoint — lib/eventSink.ts speaks the
// raw callable protocol with keepalive, see its module docstring.
// ---------------------------------------------------------------------------

/** Call shape of a wrapper: same as the SDK's HttpsCallable invocation. */
type TheaWebCall<Req, Res> = (data?: Req) => Promise<HttpsCallableResult<Res>>;

function dispatcherOp<Req, Res, O extends string>(
  dispatcher: string,
  op: O,
  timeoutMs: number,
): TheaWebCall<Req, Res> {
  const call = httpsCallable<
    TheaWebDispatchEnvelope<Req | Record<string, never>, O>,
    Res
  >(functions, dispatcher, { timeout: timeoutMs });
  // Undefined payloads (e.g. trackSignup sends nothing) become {} so the wire
  // envelope always carries an object — the BE treats {} and missing alike.
  return (data?: Req) => call({ op, payload: data ?? {} });
}

/** Wrapper for a lean op on the `theaWeb` dispatcher. */
function theaWebOp<Req, Res>(op: TheaWebOp, timeoutMs: number): TheaWebCall<Req, Res> {
  return dispatcherOp<Req, Res, TheaWebOp>(THEA_WEB_DISPATCHER, op, timeoutMs);
}

/** Wrapper for an AI op on the `theaWebAI` dispatcher. */
function theaWebAIOp<Req, Res>(op: TheaWebAIOp, timeoutMs: number): TheaWebCall<Req, Res> {
  return dispatcherOp<Req, Res, TheaWebAIOp>(THEA_WEB_AI_DISPATCHER, op, timeoutMs);
}

// Submit returns synchronously after queuing the recommendation pipeline; long
// AI work runs in async triggers, so 60s is the wire ceiling not the work ceiling.
export const submitGiftFlow: TheaWebCall<
  TheaWebSubmitGiftFlowRequest,
  TheaWebSubmitGiftFlowResponse
> = theaWebOp('submitGiftFlow', 60_000);

export const recordActivity: TheaWebCall<
  TheaWebRecordActivityRequest,
  TheaWebRecordActivityResponse
> = theaWebOp('recordActivity', 30_000);

// Add a link from any store (THE-1084 PR2). Same timeout as recordActivity —
// one Firestore write, no product lookup, no model call.
export const recordAddedLink: TheaWebCall<
  TheaWebRecordAddedLinkRequest,
  TheaWebRecordAddedLinkResponse
> = theaWebOp('recordAddedLink', 30_000);

// "More like this" rail: nearest-neighbor lookup for a seed product. Read-only
// KNN over precomputed embeddings; 15s is generous for an 8-item read.
//
// This is the rail's FALLBACK, not its source — `loadSimilarProducts` reads the
// materialized doc first and only calls this when a product has no rail yet. So
// it wants warmth it cannot justify buying: a cold KNN standing in for a single
// Firestore read is a visible pause. Riding the shared warm dispatcher is how
// it gets that for free (THE-482).
export const similarProducts: TheaWebCall<
  TheaWebSimilarProductsRequest,
  TheaWebSimilarProductsResponse
> = theaWebOp('similarProducts', 15_000);

// Gift Guide Hub search bar (THE-586). Two Typesense queries, no model call.
//
// 10s, which is a LOT for a search the server answers in ~50ms — and the
// margin is not padding, it is the remote embedder. Typesense embeds the query
// text through OpenAI, so a string it has never seen costs ~700ms while a
// repeat of the same string costs ~50ms (measured: 733ms p50 cold, 48ms p50
// warm, n=12 each). Popular queries are fast; a novel sentence is not, and the
// timeout has to cover the novel one plus a bad network.
//
// Deliberately NOT on the AI dispatcher's 60s/300s budgets: this path cannot
// legitimately take that long, so a long timeout would only hide a fault.
export const searchGifts: TheaWebCall<
  TheaWebSearchGiftsRequest,
  TheaWebSearchGiftsResponse
> = theaWebOp('searchGifts', 10_000);

// Inline guide enrichment shares the warm dispatcher without fetching products.
export const searchGuides: TheaWebCall<
  TheaWebSearchGuidesRequest,
  TheaWebSearchGuidesResponse
> = theaWebOp('searchGuides', 10_000);

// A hub collection's deduped product union (THE-817). Two Firestore reads and
// one keyword-only Typesense search.
//
// 10s, matching searchGifts, but for the opposite reason — and the margin here
// IS padding rather than a known cost. This op runs no vector arm at all
// (`q: *` with an id filter), so there is no remote embedder in the path and
// no ~700ms novel-query case to cover: the server answers in roughly the time
// a filtered read takes. What the budget has to cover is the RESPONSE, which
// is the largest this service returns — a whole collection of products, up to
// 51 today — over a bad mobile network.
//
// Deliberately not longer: this path cannot legitimately take 30s, and a
// generous timeout on a fast op only hides a fault.
export const collectionGifts: TheaWebCall<
  TheaWebCollectionGiftsRequest,
  TheaWebCollectionGiftsResponse
> = theaWebOp('collectionGifts', 10_000);

// Welcome-flow trigger: forwards a Klaviyo "Signed Up" event SERVER-SIDE. Fired
// fire-and-forget from the signup hook the moment a user goes anon→permanent.
// Same-origin, so it survives the ad-blockers that drop the browser's
// klaviyo.js (~25-40% of signups). The server reads the email + method from the
// verified auth token — the FE sends nothing. 10s is generous for one POST.
export const trackSignup: TheaWebCall<
  TheaWebTrackSignupRequest,
  TheaWebTrackSignupResponse
> = theaWebOp('trackSignup', 10_000);

// Soft email capture from the board's bottom sheet (tranche 2). Stores the
// volunteered address on the anon user doc (unverified) and forwards a Klaviyo
// `board_saved` event so the re-engagement flow can email the user their board
// link. No account change, no code — verification is a later on-demand upgrade.
// Same-origin, so it survives the ad-blockers that drop klaviyo.js. 10s is
// generous for one write + one POST.
export const captureEmail: TheaWebCall<
  TheaWebCaptureEmailRequest,
  TheaWebCaptureEmailResponse
> = theaWebOp('captureEmail', 10_000);

// Reports the device's IANA timezone (Intl.DateTimeFormat) so server-side
// jobs can reason about the user's LOCAL day — first consumer is the day-of
// birthday email's send-window rule. Fire-and-forget telemetry-grade write;
// the caller (useSyncTimezone) only re-sends when the value changes.
export const syncTimezone: TheaWebCall<
  TheaWebSyncTimezoneRequest,
  TheaWebSyncTimezoneResponse
> = theaWebOp('syncTimezone', 10_000);

// Cookie-consent geo gate. Fires once per session, BEFORE any tag is allowed
// to fire, so it sits directly in front of the banner decision.
//
// 5s rather than the usual 10-30s: this is a single in-memory range lookup on
// a warm dispatcher, and a visitor is waiting on it to learn whether they get
// a banner. `consentGeo.ts` races it against its own shorter deadline too —
// the failure mode we must avoid is a hung call that leaves someone never
// asked AND never tracked. Every failure path falls back to showing the banner.
export const resolveConsentGeo: TheaWebCall<
  TheaWebResolveConsentGeoRequest,
  TheaWebResolveConsentGeoResponse
> = theaWebOp('resolveConsentGeo', 5_000);

// In-place mutation of an existing recommendation's carousel session — the
// lightweight alternative to a full regenerate for the board's chip strip.
// operation:"addChip" runs ONE chip's product search + curation and appends its
// carousel (preserving every other carousel; idempotent — a repeat call for a
// complete chip returns alreadyPresent=true without re-running the agent).
// operation:"removeChip" drops that chip's carousel. Both persist the change
// into recommendation.input.interests. No navigation — the FE's live listeners
// on the session + recommendation docs reflect the mutation.
//
// 60s timeout: addChip runs the carousel agent (BE budget ~3-5s; 60s is the
// failsafe ceiling). removeChip returns in well under a second.
// Routed through the WARM `theaWebAI` dispatcher since 2026-09-01 — this call
// is the one Kate measured at 117.83s cold, which is what made the board look
// frozen for up to 2 minutes after adding a chip.
export const mutateRecommendation: TheaWebCall<
  TheaWebMutateRecommendationRequest,
  TheaWebMutateRecommendationResponse
> = theaWebAIOp('mutateRecommendation', 60_000);

// Kicks off the carousel agent for a quiz submission (initial or regenerate)
// and for the standalone carousel page. Fire-and-forget at every call site:
// the agent streams progressive results into carouselSessions and the UI
// renders off a live listener, so this promise is only awaited for its error.
//
// Payload is loose by design — the three call sites send overlapping but
// different key sets (only the quiz path sends `occasion`), and the BE parses
// it in get_carousel_feed.py rather than from a shared schema.
//
// 300s timeout matches the BE's agent budget. Routed through the WARM
// `theaWebAI` dispatcher since 2026-09-01; it was previously a dedicated cold
// callable, putting a ~118s boot in front of quiz results.
export const getCarouselFeed: TheaWebCall<
  Record<string, unknown>,
  unknown
> = theaWebAIOp('getCarouselFeed', 300_000);

// v4 guest→account claim (#152). Dispatcher ops since 2026-09-01: the claim
// page is a first-open path off an email link, so the visitor was staring at a
// cold boot before she even had an account. Lean enough to ride the existing
// warm instance at no extra cost.
// Preview reads the guest's boards (no consume); claim burns the attach grant
// and copies the subtree. Both raise NOT_FOUND on any bad/dead token, UNAVAILABLE
// under the kill switch, and preview raises RESOURCE_EXHAUSTED past the rate cap —
// the caller distinguishes those three.
export const getClaimPreview: TheaWebCall<
  TheaWebGetClaimPreviewRequest,
  TheaWebGetClaimPreviewResponse
> = theaWebOp('getClaimPreview', 15_000);

export const claimBoard: TheaWebCall<
  TheaWebClaimBoardRequest,
  TheaWebClaimBoardResponse
> = theaWebOp('claimBoard', 30_000);

// Add a person without the quiz (THE-698 WS1). `requestId` must be minted
// once per confirm, not per render — see the type's own comment; a fresh id
// per render defeats the server's dedupe and adds the same person twice.
export const createRecipient: TheaWebCall<
  TheaWebCreateRecipientRequest,
  TheaWebCreateRecipientResponse
> = theaWebOp('createRecipient', 30_000);

export const updateRecipient: TheaWebCall<
  TheaWebUpdateRecipientRequest,
  TheaWebUpdateRecipientResponse
> = theaWebOp('updateRecipient', 30_000);

// Dismiss an alert from the bell dropdown. Idempotent — re-dismissing an
// already-dismissed alert returns success without a write.
export const updateAlert: TheaWebCall<
  TheaWebUpdateAlertRequest,
  TheaWebUpdateAlertResponse
> = theaWebOp('updateAlert', 30_000);

// Merge fans out a WriteBatch across recipients/recommendations/giftActivities
// then deletes the source — generous timeout matches the worst-case fan-out.
export const mergeGiftFlow: TheaWebCall<
  TheaWebMergeGiftFlowRequest,
  TheaWebMergeGiftFlowResponse
> = theaWebOp('mergeGiftFlow', 120_000);

// Mints a single-use, 60s-TTL merge token while the caller is still anon.
// The returned token is paired with the anon uid in `mergeGiftFlow({fromUid, token})`
// after the caller authenticates as the surviving permanent uid.
export const mintMergeToken: TheaWebCall<
  TheaWebMintMergeTokenRequest,
  TheaWebMintMergeTokenResponse
> = theaWebOp('mintMergeToken', 30_000);

// NOTE: theaWebLogEvents has no wrapper here — `lib/eventSink.ts` POSTs the
// callable protocol directly with `keepalive: true` so batches survive
// same-tab navigation (mobile click-outs). See its module docstring.

// Mints (or reuses the active) shareable-board link for a recipient. Fires on
// the Share tap right before the native share sheet — kept snappy.
export const createShareLink: TheaWebCall<
  TheaWebCreateShareLinkRequest,
  TheaWebCreateShareLinkResponse
> = theaWebOp('createShareLink', 30_000);

// Resolves a shared board for a friend opening the link (read-only view of the
// owner's SAVED items). Possession of the shareId is the access grant.
export const getSharedBoard: TheaWebCall<
  TheaWebGetSharedBoardRequest,
  TheaWebGetSharedBoardResponse
> = theaWebOp('getSharedBoard', 30_000);

// Revokes a share link the caller owns (idempotent).
export const revokeShareLink: TheaWebCall<
  TheaWebRevokeShareLinkRequest,
  TheaWebRevokeShareLinkResponse
> = theaWebOp('revokeShareLink', 30_000);

// Mints (or reuses the active) session-resume token for the caller's ANON uid.
// Fired lazily after board load in in-app browsers only (useResumeToken) —
// not user-blocking, so a cold start just delays the URL decoration.
export const mintResumeToken: TheaWebCall<
  TheaWebMintResumeTokenRequest,
  TheaWebMintResumeTokenResponse
> = theaWebOp('mintResumeToken', 30_000);

// Exchanges a board URL's `rt` token for a custom token restoring the original
// anon session after an in-app browser wiped local auth storage. Blocks the
// returning user's board render — rides the warm dispatcher instance.
export const redeemResumeToken: TheaWebCall<
  TheaWebRedeemResumeTokenRequest,
  TheaWebRedeemResumeTokenResponse
> = theaWebOp('redeemResumeToken', 30_000);

// OTP sign-in — step 1: request a short-lived 6-digit email code. Always
// returns success on a well-formed request (no account-enumeration oracle); the
// server sends the code + rate-limits per email/IP. Fired on the user's "Send
// me a code" tap, so kept snappy.
export const requestEmailOtp: TheaWebCall<
  TheaWebRequestEmailOtpRequest,
  TheaWebRequestEmailOtpResponse
> = theaWebOp('requestEmailOtp', 30_000);

// OTP sign-in — step 2: verify the 6-digit code. On success the server signs
// the caller into (login), upgrades in place (anon→permanent, same uid), or
// creates the account for the email and returns a Firebase custom token to
// pass straight to signInWithCustomToken. Invalid/expired/exhausted codes are
// errors. 30s covers the getUserByEmail + custom-token mint round-trip.
export const verifyEmailOtp: TheaWebCall<
  TheaWebVerifyEmailOtpRequest,
  TheaWebVerifyEmailOtpResponse
> = theaWebOp('verifyEmailOtp', 30_000);

// ---------------------------------------------------------------------------
// Self-serve account deletion (GDPR Art. 17 / CCPA). Two ops, never one.
// ---------------------------------------------------------------------------

// Mails a 6-digit confirmation code (permanent accounts) or reports that the
// anonymous typed-phrase path applies. DESTROYS NOTHING — that is the whole
// reason it is a separate op from the one below. 15s covers a Firestore write
// plus one Klaviyo POST.
export const requestAccountDeletion: TheaWebCall<
  TheaWebRequestAccountDeletionRequest,
  TheaWebRequestAccountDeletionResponse
> = theaWebOp('requestAccountDeletion', 15_000);

// The irreversible one. Verifies the proof, then walks the purge matrix
// (Firestore subtree, boards, share/grant/resume sentinels, Klaviyo profile)
// and deletes the Auth user. 120s matches the dispatcher's server ceiling: a
// large account's walk is the slowest thing this app asks of the backend, and a
// client timeout here would leave the user staring at an error while their
// account is already being deleted.
export const confirmAccountDeletion: TheaWebCall<
  TheaWebConfirmAccountDeletionRequest,
  TheaWebConfirmAccountDeletionResponse
> = theaWebOp('confirmAccountDeletion', 120_000);

// Trebuchet selective-launch flags. The server evaluates EVERY registered flag
// for the caller and returns booleans only, so no targeting rule (allow/deny
// lists, percentage rollout, internal-only) ever reaches the client. Called
// once per auth state by TrebuchetProvider — not per flag, not per consumer.
// Fail closed: any error means every flag reads as off. 15s is generous for
// one evaluated read on the warm dispatcher.
export const getLaunchFlags: TheaWebCall<
  TheaWebGetLaunchFlagsRequest,
  TheaWebGetLaunchFlagsResponse
> = theaWebOp('getLaunchFlags', 15_000);

/** Verified read-only routing before the one Firebase Apple credential exchange. */
export const resolveAppleAccount: TheaWebCall<
  { idToken: string; rawNonce: string },
  { action: 'signIn' | 'link' }
> = theaWebOp('resolveAppleAccount', 15_000);

// Private preference payload: no analytics, event sink, or error logging.
export const updateHolidayPrefs = theaWebOp<
  { hiddenHolidays: string[] },
  { success: boolean; hiddenHolidays: string[] }
>('updateHolidayPrefs', 30_000);

export const getBoardUsage: TheaWebCall<TheaWebGetBoardUsageRequest, TheaWebGetBoardUsageResponse> = theaWebOp('getBoardUsage', 15_000);

/** Explicit legal agreement evidence. The server derives identity and timestamps. */
export const recordLegalAgreement = theaWebOp<import('./legal/legalTypes').LegalAttempt & { accountUid: string }, {
  agreementId: string; acceptedDocumentId: string;
}>('recordLegalAgreement', 20_000);
